import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getDocuments } from 'services/documentsApi'
import { useGetBrands } from 'services/brandsApi'
import { format } from 'date-fns'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { TableSearch } from 'components/Filters/TableSearch'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { DocumentsFilterset } from 'components/DocumentLibrary/DocumentsFilterset'
import CustomCheckbox from 'components/Checkbox/checkbox'
import { cn } from 'components/UI/cn'
import type { Document } from 'types/documents'

const FILTER_IDENTIFIER = 'DOCUMENTS_IMAGE_PICKER' as const
const PAGE_SIZE = 100

interface SelectableImageTableProps {
  selectedImageId: number | null
  onSelectImage: (imageId: number | null, imageUrl?: string) => void
}

export function SelectableImageTable({
  selectedImageId,
  onSelectImage,
}: SelectableImageTableProps) {
  const imageFilterStore = filterStoreRepo.getStore(FILTER_IDENTIFIER)
  const filterParams = useFilterParams(imageFilterStore)

  // Reset filter state on mount so previous search/filter doesn't leak between modal opens
  useEffect(() => {
    imageFilterStore.setState({ search: {}, valueFilters: {} })
  }, [imageFilterStore])

  const filterAndSortParams = useMemo(
    () => ({
      ...filterParams,
      is_image: true,
      ordering: '-created',
    }),
    [filterParams]
  )

  const { data, isLoading } = useQuery({
    queryKey: ['documents-image-picker', filterAndSortParams],
    queryFn: () =>
      getDocuments({
        ...filterAndSortParams,
        page_size: PAGE_SIZE,
      }),
  })

  const { data: brandsData } = useGetBrands({ page_size: 1000 })

  // Map document ID → brand name for images already used as a brand logo
  const imageIdToBrand = useMemo(() => {
    const map = new Map<number, string>()
    for (const brand of brandsData?.results ?? []) {
      if (brand.image_id) {
        map.set(brand.image_id, brand.name)
      }
    }
    return map
  }, [brandsData])

  const documents = data?.results ?? []

  // When documents load, resolve the correct file URL for any pre-selected image
  // (brand.image_url from the API may be a relative path that doesn't render).
  useEffect(() => {
    if (selectedImageId && documents.length > 0) {
      const match = documents.find((doc) => doc.id === selectedImageId)
      if (match?.file) {
        onSelectImage(selectedImageId, match.file)
      }
    }
  }, [documents, selectedImageId, onSelectImage])

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MM/dd/yyyy')
    } catch {
      return dateString
    }
  }

  const handleRowClick = (doc: Document) => {
    if (doc.id === selectedImageId) {
      onSelectImage(null)
    } else {
      onSelectImage(doc.id, doc.file)
    }
  }

  return (
    <div className="rounded border border-gray-200 bg-surface-raised">
      {/* Search + Filters bar */}
      <div className="flex flex-col gap-2 px-3 pt-3">
        <div className="flex w-full items-center gap-2">
          <TableSearch
            className="flex-1"
            filterStore={imageFilterStore}
            searchPlaceholder="Search images..."
          />
          <DocumentsFilterset filterIdentifier={FILTER_IDENTIFIER} />
        </div>
        <FilterChips
          identifier={FILTER_IDENTIFIER}
          store={imageFilterStore}
          clearAllButton
          showActive
        />
      </div>

      {/* Table */}
      <div className="mt-2 min-h-[300px] max-h-[300px] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-surface text-left text-xs font-medium uppercase text-gray-500">
            <tr>
              <th className="w-[40px] border-b border-line-subtle px-3 py-2" />
              <th className="w-[60px] border-b border-line-subtle px-2 py-2">
                Preview
              </th>
              <th className="border-b border-line-subtle px-3 py-2">Name</th>
              <th className="w-[120px] border-b border-line-subtle px-3 py-2">
                Brand
              </th>
              <th className="w-[80px] border-b border-line-subtle px-3 py-2">
                Type
              </th>
              <th className="w-[110px] border-b border-line-subtle px-3 py-2">
                Uploaded
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            )}
            {!isLoading && documents.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-gray-500">
                  No images found. Upload images in the Document Library first.
                </td>
              </tr>
            )}
            {!isLoading &&
              documents.map((doc) => {
                const isSelected = doc.id === selectedImageId
                return (
                  <tr
                    key={doc.id}
                    onClick={() => handleRowClick(doc)}
                    className={cn(
                      'cursor-pointer border-b border-line-subtle transition-colors',
                      isSelected
                        ? 'bg-surface-raised hover:bg-sage-lt'
                        : 'bg-white hover:bg-sage-extra-lt'
                    )}
                  >
                    <td
                      className="px-3 py-2 text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <CustomCheckbox
                        checked={isSelected}
                        onChange={() => handleRowClick(doc)}
                      />
                    </td>
                    <td className="px-2 py-1">
                      {doc.file ? (
                        <img
                          src={doc.file}
                          alt={doc.name}
                          className="h-10 w-10 rounded object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-100 text-xs text-gray-400">
                          N/A
                        </div>
                      )}
                    </td>
                    <td className="max-w-0 truncate px-3 py-2" title={doc.name}>
                      {doc.name}
                    </td>
                    <td className="truncate px-3 py-2 text-gray-600">
                      {imageIdToBrand.get(doc.id) ?? '-'}
                    </td>
                    <td className="px-3 py-2 uppercase text-gray-600">
                      {doc.file_extension || '-'}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {doc.created ? formatDate(doc.created) : '-'}
                    </td>
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
